<script lang="ts">
    import MyOrderDetails from "./MyOrderDetails.svelte"

    import { location } from "../../i18n"

    import { fade } from "svelte/transition"
    import { formatPriceCurrency, langField } from "../../i18n"
    import Index from "../SEO/Index.svelte"
    import { spaNavigate } from "../../functionTools/navigation"
    import { myOrdersToken } from "../../functionTools/cart"
    import { _ } from "svelte-i18n"
    import { getMyOrders } from "../../api"
    import BarLoader from "svelte-loading-spinners/BarLoader.svelte"
    import CartItemImage from "../ecommerce/cart/CartItemImage.svelte"

    interface Props {
        token: string
    }

    let { token }: Props = $props()

    let offset = $state(0)
    let hasMore = $state(false)
    let loading = $state(false)
    let count = 0
    let orders: Order[] = $state([])

    const setOrders = (_orders: { data: Order[]; count: number }) => {
        orders = [...orders, ..._orders.data]
        count = _orders.count
        hasMore = orders.length < count
        offset = orders.length
    }

    $effect(() => {
        $myOrdersToken = token
    })

    let error: { data?: { status?: number } } = $state()
    const load = async () => {
        hasMore = false
        loading = true
        try {
            const _orders = await getMyOrders(token, null, "-insertTime", 5, offset)
            setOrders(_orders)
        } catch (e) {
            error = e
        }

        loading = false
    }

    const gotoLinkRequest = () => {
        $myOrdersToken = ""
        spaNavigate("/order")
    }

    let oldToken: string = $state()
    $effect(() => {
        if (token != oldToken && token) {
            offset = 0
            load()
            oldToken = token
        }
    })
</script>

{#if $location?.path?.split("/order/")[1].split("/").length > 1}
    <MyOrderDetails {token} id={$location?.path?.split("/order/")[1].split("/")[1]} />
{:else}
    <Index noIndex={true} title={$_("my-orders.my-orders")} metaDescription={$_("meta.description")} />
    <section id="myOrdersPage" class="section-type-1 section_padding checkout">
        <div class="container">
            <h1>{$_("my-orders.my-orders")}</h1>
            <p>{@html $_("my-orders.intro-html")}</p>
            <p>
                <a
                    href="/order"
                    onclick={(e) => {
                        e.preventDefault()
                        gotoLinkRequest()
                    }}>{$_("my-orders.click-link-prompt")}</a
                >
            </p>

            {#if token}
                {#if error}
                    {#if error.data?.status == 403}
                        <p>
                            <b style="color: var(--error);">{$_("my-orders.access-denied")}</b>
                        </p>
                        <p>{$_("my-orders.link-is-invalid")}</p>
                    {:else}
                        {JSON.stringify(error)}
                    {/if}
                {:else}
                    <div class="myorders">
                        {#each orders as order (order.id)}
                            <div class="order" in:fade={{}}>
                                <div class="product_images">
                                    {#each order.cart.filter((el) => !el._parent_uuid) || [] as item, idx}
                                        {#if idx < 2}
                                            <CartItemImage {item}>
                                                {#snippet additionalContentSnippet()}
                                                    {#if idx == 1 && order.cart.length > 2}
                                                        <div class="morepics">
                                                            +
                                                            {order.cart.length - 1}
                                                        </div>
                                                    {/if}
                                                {/snippet}
                                            </CartItemImage>
                                        {/if}
                                    {/each}
                                </div>
                                <div class="order_details">
                                    <div class="header">
                                        <h3>
                                            {$_("my-orders.order-from-date", {
                                                values: {
                                                    date: new Date(order.insertTime).toLocaleDateString(),
                                                },
                                            })}
                                        </h3>
                                        <div class="ordernum">
                                            {$_("my-orders.order-number-short")}:
                                            {order.sequence}
                                        </div>
                                    </div>
                                    <div class="price">
                                        {$formatPriceCurrency(order.sum?.totalGross)}
                                    </div>
                                    <a href="/order/{token}/{order.id}" class="button primary"
                                        >{$_("my-orders.view-order")}</a
                                    >
                                </div>
                            </div>
                        {:else}
                            <p>
                                <b>{@html $_("my-orders.no-order-for-email-html")}</b>
                            </p>
                        {/each}
                        {#if hasMore}
                            <div class="center_btn">
                                <!-- svelte-ignore a11y_invalid_attribute -->
                                <a
                                    href=""
                                    class="button primary"
                                    onclick={(e) => {
                                        e.preventDefault()
                                        load()
                                    }}>{$_("my-orders.load-more")}</a
                                >
                            </div>
                        {/if}
                    </div>
                    {#if loading}
                        <BarLoader />
                    {/if}
                {/if}
            {/if}
        </div>
    </section>
{/if}

<style lang="less">
    @import "../../css/variables.less";
    .myorders {
        .center_btn {
            margin-top: 1rem;
        }
        .order {
            border-bottom: dotted 1px #ccc;
            padding: 40px 0px;
            .flex();
            @media (max-width: 420px) {
                flex-direction: column;
            }
            .product_images {
                width: 40%;
                margin: -5px;
                .flex();
                @media (max-width: 900px) {
                    flex-direction: column;
                }
                @media (max-width: 420px) {
                    width: 100%;
                    margin-bottom: 20px;
                    flex-direction: column;
                }

                .morepics {
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: var(--surface-3);
                    color: var(--on-surface-3);
                    opacity: 0.8;
                    .flex();
                    align-items: center;
                    justify-content: center;
                    font-size: 32px;
                    @media (max-width: 768px) {
                        font-size: 22px;
                    }
                }
            }
            .order_details {
                width: 60%;
                padding-left: 30px;
                @media (max-width: 420px) {
                    width: 100%;
                    padding-left: 0px;
                }
                .header {
                    margin-bottom: 30px;
                    h3 {
                        margin: 0px;
                    }
                    .ordernum {
                        font-size: 14px;
                    }
                }
                .price {
                    color: var(--primary);
                    font-size: 1.6rem;
                    margin-bottom: 10px;
                }
            }
        }
    }
</style>
